import { useEffect, useState } from "react";

export type ViewportInsetMetrics = {
  /** Visible layout height for sizing (accounts for dynamic browser UI where possible). */
  layoutViewportHeight: number;
  /** Distance from layout bottom to physical bottom when the keyboard shrinks the visual viewport. */
  keyboardInsetBottom: number;
  safeAreaTop: number;
  safeAreaBottom: number;
};

function readSafeAreaInset(edge: "top" | "bottom"): number {
  if (typeof window === "undefined" || typeof getComputedStyle === "undefined") {
    return 0;
  }
  const v = getComputedStyle(document.documentElement).getPropertyValue(
    `env(safe-area-inset-${edge})`
  );
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Keyboard and safe-area metrics for mobile composer / chat viewport. Prefers VisualViewport when available.
 */
export function useViewportKeyboardInsets(enabled: boolean): ViewportInsetMetrics {
  const [metrics, setMetrics] = useState<ViewportInsetMetrics>(() => ({
    layoutViewportHeight: typeof window !== "undefined" ? window.innerHeight : 0,
    keyboardInsetBottom: 0,
    safeAreaTop: 0,
    safeAreaBottom: 0,
  }));

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    const vv = window.visualViewport;

    const update = () => {
      const safeAreaTop = readSafeAreaInset("top");
      const safeAreaBottom = readSafeAreaInset("bottom");
      if (vv) {
        const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        setMetrics({
          layoutViewportHeight: vv.height,
          keyboardInsetBottom: inset,
          safeAreaTop,
          safeAreaBottom,
        });
      } else {
        setMetrics({
          layoutViewportHeight: window.innerHeight,
          keyboardInsetBottom: 0,
          safeAreaTop,
          safeAreaBottom,
        });
      }
    };

    update();
    window.addEventListener("resize", update);
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);

    return () => {
      window.removeEventListener("resize", update);
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
    };
  }, [enabled]);

  return metrics;
}
